import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import globalStore from '@/services/global.state';
import {ActivityAwardItem} from '../../type';
import {AwardTableVariant, buildAwardTableRows} from './rewards.utils';

type RewardsCardProps =
  | {
      source: 'awardList';
      awardList: ActivityAwardItem[];
      variant: AwardTableVariant;
    }
  | {
      source: 'template';
      data: string;
    };

const headerKeys: Record<AwardTableVariant, {amount: string; reward: string}> =
  {
    betting: {
      amount: 'promotionsV2.rewardTable.bettingAmount',
      reward: 'promotionsV2.rewardTable.bettingReward',
    },
    recharge: {
      amount: 'promotionsV2.rewardTable.rechargeAmount',
      reward: 'promotionsV2.rewardTable.rechargeReward',
    },
    withdraw: {
      amount: 'promotionsV2.rewardTable.withdrawAmount',
      reward: 'promotionsV2.rewardTable.withdrawReward',
    },
    count: {
      amount: 'promotionsV2.rewardTable.times',
      reward: 'promotionsV2.rewardTable.rewards',
    },
    generic: {
      amount: 'promotionsV2.rewardTable.amount',
      reward: 'promotionsV2.rewardTable.rewards',
    },
  };

const RewardsCard = (props: RewardsCardProps) => {
  const {t} = useTranslation();
  const table = useMemo(() => {
    if (props.source === 'template') {
      try {
        const plainText = props.data.replace(/<[^>]*>?/gm, '\n');
        const [header = [], ...rows] = plainText
          .split('\n')
          .filter(item => item.trim())
          .map(item => item.split(','));
        return {header, rows};
      } catch (error) {
        return {header: [], rows: []};
      }
    }

    const labels = headerKeys[props.variant];
    const rows = buildAwardTableRows(
      props.awardList,
      props.variant,
      globalStore.currency,
    );
    return {
      header: [t(labels.amount), t(labels.reward)],
      rows,
    };
  }, [props, t]);

  return (
    <div className="mx-4 bg-white rounded-xl flex flex-col p-3 gap-3 mb-2">
      <div className="flex flex-row items-center gap-2">
        <div className="w-1 h-3 bg-c1" />
        <div className="text-t1 text-sm font-bold capitalize">
          {t('promotionsV2.eventRewards')}
        </div>
      </div>
      <div className={'flex flex-col flex-1 text-t2 text-sm font-bold'}>
        <table className="w-full">
          <thead className="text-xs bg-[#C6D5E1] capitalize">
            <tr>
              {table.header.map((item, index) => (
                <th key={index} className="text-center py-2">
                  {item}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, index) => (
              <tr
                key={index}
                className="bg-[#F3F9FF] odd:bg-[#E3F1FD] divide-x divide-[#C6D5E1]">
                {row.map((item, cellIndex) => (
                  <td key={cellIndex} className="text-center py-2">
                    {item}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RewardsCard;
